import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Typography,
  Avatar,
  Tooltip,
  Grid,
  InputBase,
  Badge,
  useTheme,
  useMediaQuery
} from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { Close } from '@mui/icons-material';
import { base_logo_url } from '../../apis/constants';
import MinimizeIcon from '@mui/icons-material/Minimize';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import DoneAllIcon from '@mui/icons-material/DoneAll';

const FloatingChatSystem = ({
  chatOrder,
  handleMarkAsReadMsgs,
  openChats,
  chatMinimized,
  setChatMinimized,
  toggleChatMinimize,
  handleOpenChat,
  shipperSessionId,
  chatData,
  ratingCarriers,
  shipperApiMsgs,
  handleShowUserInputMsgs,
  handleUserInput,
  userInput
}) => {
  const bottomRef = useRef({});
  const theme = useTheme();

  // get current screen size with breakpoints
  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));
  const isMd = useMediaQuery(theme.breakpoints.only('md'));
  const isLg = useMediaQuery(theme.breakpoints.only('lg'));
  const isXl = useMediaQuery(theme.breakpoints.only('xl'));

  let boxWidth = 350; // default
  if (isXs) boxWidth = 200;
  else if (isSm) boxWidth = 250;
  else if (isMd) boxWidth = 290;
  else if (isLg) boxWidth = 320;
  else if (isXl) boxWidth = 350;

  const spacing = 10; // gap between chat boxes

  const responedCarrriers = useMemo(() => {
    const responded = ratingCarriers.filter(
      carriers => carriers.carrierResponseStatus === 'RESPONDED'
    );
    return responded;
  }, [ratingCarriers]);

  const scrollToBottom = () => {
    bottomRef.current &&
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const prevMsgCountRef = useRef({});

  useEffect(() => {
    if (!shipperSessionId) return;

    const matchedCarrier = ratingCarriers.find(
      carrier => carrier.chatDetails?.[0]?.sessionId === shipperSessionId
    );

    const currentCount = matchedCarrier?.chatDetails?.length || 0;
    const prevCount = prevMsgCountRef.current[shipperSessionId] || 0;

    if (currentCount > prevCount) {
      const ref = bottomRef.current?.[shipperSessionId];
      if (ref) ref.scrollIntoView({ behavior: 'smooth' });
    }

    prevMsgCountRef.current[shipperSessionId] = currentCount;
  }, [shipperSessionId, ratingCarriers]);

  const removeTableCss = finalRes => {
    const regexPattern = /<style[^>]*>([\s\S]*?)<\/style>/gi;

    // Remove table styles including borders
    const updatedHtml = finalRes.replace(regexPattern, (match, styles) => {
      const stylesWithoutTable = styles.replace(
        /(?:table|td|th)\s*\{[^}]*\}/gi,
        ''
      );
      return `<style>${stylesWithoutTable} table { font-size: 12px; } td { padding: 5px; }</style>`;
    });
    return updatedHtml;
  };

  const handleDateTimeShow = (timeStamp, type) => {
    if (timeStamp !== null) {
      const dateTimeString = timeStamp + 'Z';
      const dateTime = new Date(dateTimeString);
      const date = new Date(dateTimeString);

      const dateOptions = {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      };

      const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const timeOptions = new Intl.DateTimeFormat('default', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZone: userTimeZone
      }).format(dateTime);

      const formattedDate = date.toLocaleDateString(undefined, dateOptions);

      if (type === 'time') {
        return timeOptions;
      }
      if (type === 'date') {
        return formattedDate;
      }
    }
  };

  const handleCorresHistory = apiRes => {
    const fontSizeToUpdate = removeTableCss(apiRes);
    return fontSizeToUpdate;
  };

  const handleInputChange = (id, value) => {
    handleUserInput(id, value);
  };

  const handleSendMessage = id => {
    handleShowUserInputMsgs(id);
  };

  let mappedChats = chatOrder
    .map(sessionId =>
      ratingCarriers.find(
        carrier => carrier?.chatDetails?.[0]?.sessionId === sessionId
      )
    )
    .filter(Boolean);

  // 🔹 Sort by chatOrder
  mappedChats = mappedChats.sort((a, b) => {
    const aSession = a.chatDetails[0]?.sessionId;
    const bSession = b.chatDetails[0]?.sessionId;
    return chatOrder.indexOf(aSession) - chatOrder.indexOf(bSession);
  });

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        right: 350,
        display: 'flex',
        height: 600,
        gap: 2,
        zIndex: 1300
      }}
    >
      {mappedChats.map((carrier, index) => {
        const sessionId = carrier?.chatDetails?.[0]?.sessionId;
        if (!sessionId || !openChats[sessionId]) return null;

        const isMinimized = chatMinimized[sessionId] === true;

        return (
          <Paper
            key={`chat-${sessionId}`}
            elevation={5}
            sx={{
              width: Math.max(150, 350 - (mappedChats.length - 1) * 5),
              borderRadius: 2,
              height: isMinimized ? '60px' : 600,
              position: 'absolute',
              bottom: 0,
              // ✅ Keep newest at rightmost edge, others stack left
              right: (mappedChats.length - 1 - index) * (boxWidth + spacing),
              pointerEvents: 'auto',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              mb: 0,
              border: '1px solid #ddd',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              backgroundColor: '#fff',
              transition: 'right 0.3s ease-in-out, width 0.3s ease-in-out'
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1,
                backgroundColor: '#f4f6f8',
                borderBottom: '1px solid #ddd',
                cursor: 'pointer',
                minHeight: 56
              }}
            >
              <Box
                display='flex'
                alignItems='center'
                gap={1}
                onClick={() =>
                  isMinimized && toggleChatMinimize(sessionId, carrier)
                }
              >
                <Avatar
                  src={base_logo_url + carrier?.chatDetails[0].actionByImage}
                  sx={{ width: 32, height: 32 }}
                />
                <Typography sx={{ mr: 0.4 }} fontWeight={600} fontSize={14}>
                  {carrier?.chatDetails[0].actionByName}
                </Typography>
                {isMinimized && (
                  <Badge
                    badgeContent={
                      (carrier?.chatDetails ?? []).filter(
                        m => m.actionByFlag === 'CARRIER' && m.isRead === false
                      ).length
                    }
                    color='error'
                    sx={{
                      '& .MuiBadge-badge': {
                        fontSize: 10,
                        height: 18,
                        minWidth: 18
                      }
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Tooltip title={isMinimized ? 'Expand' : 'Minimize'}>
                  <IconButton
                    sx={{ mb: !isMinimized ? 1.4 : 0 }}
                    size='small'
                    onClick={() => toggleChatMinimize(sessionId, carrier)}
                  >
                    {isMinimized ? (
                      <OpenInFullIcon fontSize='small' />
                    ) : (
                      <MinimizeIcon fontSize='small' />
                    )}
                  </IconButton>
                </Tooltip>

                <Tooltip title='Close'>
                  <IconButton
                    size='small'
                    onClick={() => {
                      handleOpenChat(sessionId, false, carrier);
                      setChatMinimized(prev => {
                        const newState = { ...prev };
                        delete newState[sessionId];
                        return newState;
                      });
                    }}
                  >
                    <Close fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Chat Body */}
            {!isMinimized && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  pointerEvents: 'auto'
                }}
              >
                <Grid
                  className='Chat_Child'
                  sx={{
                    px: 1.5,
                    py: 1,
                    overflow: 'auto',
                    flex: 1,
                    maxHeight: 'calc(600px - 56px - 60px)'
                  }}
                >
                  {carrier?.chatDetails?.map((carrierRes, idx) => (
                    <Grid
                      key={`msg-${sessionId}-${idx}`}
                      sx={{
                        display: 'flex',
                        justifyContent:
                          carrierRes.actionByFlag.toLowerCase() === 'carrier'
                            ? 'flex-start'
                            : 'flex-end',
                        flexDirection: 'column',
                        mx: 1,
                        my: 1,
                        position: 'relative'
                      }}
                    >
                      {carrierRes.actionByFlag.toLowerCase() === 'carrier' && (
                        <Grid
                          display='flex'
                          alignItems='center'
                          gap={1}
                          mb={0.5}
                        >
                          <Avatar
                            src={base_logo_url + carrierRes?.actionByImage}
                            sx={{ width: 40, height: 40 }}
                          />
                          <Box>
                            <Typography fontWeight={600} fontSize={13}>
                              {carrierRes?.actionByName}
                            </Typography>
                            <Typography fontSize={11} color='text.secondary'>
                              {carrierRes?.actionByDesignation}
                            </Typography>
                          </Box>
                        </Grid>
                      )}

                      {carrierRes.actionByFlag.toLowerCase() === 'shipper' && (
                        <Grid
                          display='flex'
                          alignItems='center'
                          justifyContent='flex-end'
                          gap={1}
                          mb={0.5}
                        >
                          <Box textAlign='right'>
                            <Typography fontWeight={600} fontSize={13}>
                              {carrierRes?.actionByName}
                            </Typography>
                            <Typography fontSize={11} color='text.secondary'>
                              {carrierRes?.actionByDesignation}
                            </Typography>
                          </Box>
                          <Avatar
                            src={base_logo_url + carrierRes?.actionByImage}
                            sx={{ width: 40, height: 40 }}
                          />
                        </Grid>
                      )}

                      <Grid
                        sx={{
                          backgroundColor:
                            carrierRes.actionByFlag.toLowerCase() === 'shipper'
                              ? '#e0f7fa'
                              : '#eceff1',
                          borderRadius:
                            carrierRes.actionByFlag.toLowerCase() === 'shipper'
                              ? '12px 0px 12px 12px'
                              : '0px 12px 12px 12px',
                          p: 1.2,
                          fontSize: 13,
                          minWidth: 150,
                          maxWidth: 250,
                          alignSelf:
                            carrierRes.actionByFlag.toLowerCase() === 'shipper'
                              ? 'flex-end'
                              : 'flex-start'
                        }}
                      >
                        <div
                          dangerouslySetInnerHTML={{
                            __html: handleCorresHistory(carrierRes.actionData)
                          }}
                        />
                        {carrierRes.actionByFlag.toLowerCase() === 'shipper' &&
                          carrierRes?.isRead && <DoneAllIcon sx={{ mt: 1 }} />}
                        <Typography
                          fontSize={10}
                          mt={0.5}
                          textAlign='right'
                          color='text.secondary'
                        >
                          {handleDateTimeShow(carrierRes.date, 'time')}
                        </Typography>
                      </Grid>
                    </Grid>
                  ))}
                  <Grid
                    ref={el => {
                      if (el) {
                        bottomRef.current[sessionId] = el;
                      }
                    }}
                  />
                </Grid>

                {/* Input */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    px: 1,
                    py: 1,
                    borderTop: '1px solid #ddd',
                    backgroundColor: '#fafafa',
                    minHeight: 60
                  }}
                >
                  <InputBase
                    placeholder='Message...'
                    value={userInput[sessionId] || ''}
                    onChange={e => handleInputChange(sessionId, e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        handleSendMessage(sessionId);
                      }
                    }}
                    fullWidth
                    sx={{ fontSize: 13, px: 1 }}
                  />
                  <SendOutlinedIcon
                    sx={{
                      transform: 'rotate(-40deg)',
                      cursor: 'pointer',
                      transition: 'transform 0.3s ease, color 0.3s ease',
                      '&:hover': {
                        color: 'secondary.main'
                      }
                    }}
                    onClick={() => handleSendMessage(sessionId)}
                    color='primary'
                  />
                </Box>
              </Box>
            )}
          </Paper>
        );
      })}
    </Box>
  );
};

export default FloatingChatSystem;
